import {
  DownloadOutlined,
  FileImageOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { Button, Modal, Spin } from 'antd';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useRef, useState } from 'react';

import {
  useLazyDownloadQuery,
  useLazyGetClientFileQuery,
} from '@app/api/ClientFiles';
import {
  LEAD_MSG_STATUS_DELETED,
  formatLeadMessageText,
  formatLeadMessageTimestamp,
  getLeadMessageStatusColor,
  getLeadMessageStatusIcon,
} from '@app/components/LeadCard/ui/MessageHistory/lib';
import { useUserPrint } from '@app/hooks';
import type { NFiles } from '@app/types';

import type { FC } from 'react';

import './LeadImageAttachment.scss';

const getImageMimeByExt = (ext: string): string => {
  switch (ext) {
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'bmp':
      return 'image/bmp';
    case 'webp':
      return 'image/webp';
    default:
      return 'image/jpeg';
  }
};

interface LeadImageAttachmentProps {
  file: NFiles.ModelClientFile;
  message: string | null;
  socialType: string | null;
  isIncoming: boolean;
  timestamp: string;
  wasEdited: boolean;
  status: string | null;
  isForwarded: boolean;
  replyMessage: string | null;
  replyMessageId: number | null;
  showStatus?: boolean;
}

export const LeadImageAttachment: FC<LeadImageAttachmentProps> = ({
  file,
  message,
  socialType,
  isIncoming,
  timestamp,
  wasEdited,
  status,
  isForwarded,
  replyMessage,
  replyMessageId,
  showStatus = true,
}) => {
  const { activeBranchId } = useUserPrint();
  const [getClientFile] = useLazyGetClientFileQuery();
  const [downloadFile] = useLazyDownloadQuery();

  const [isLightboxLoading, setIsLightboxLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);

  const isDeleted = status === LEAD_MSG_STATUS_DELETED;
  const showStatusIcon = !isIncoming || isDeleted;
  const statusIcon = showStatusIcon ? getLeadMessageStatusIcon(status) : null;
  const statusColor = getLeadMessageStatusColor(status);
  const formattedTimestamp = formatLeadMessageTimestamp(timestamp);

  const sanitizedReplyHtml = replyMessage
    ? DOMPurify.sanitize(formatLeadMessageText(replyMessage, socialType))
    : null;
  const sanitizedMessageHtml = message
    ? DOMPurify.sanitize(formatLeadMessageText(message, socialType))
    : null;

  const ext = (file.Extension ?? '').trim().toLowerCase();
  const mime = getImageMimeByExt(ext);

  const handleOpen = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (previewSrc) {
      setIsPreviewOpen(true);
      return;
    }

    setIsLightboxLoading(true);

    try {
      const result = await getClientFile({
        fileId: file.Id,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      if (!result.Data) return;

      const src = `data:${mime};base64,${result.Data}`;
      setPreviewSrc(src);
      setIsPreviewOpen(true);
    } catch {
      // silent
    } finally {
      setIsLightboxLoading(false);
    }
  };

  const handlePreviewClose = () => {
    setIsPreviewOpen(false);
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDownloading(true);

    try {
      const buffer = await downloadFile({
        clientId: file.ClientId,
        fileId: file.Id,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      const blob = new Blob([buffer], { type: mime });
      const url = URL.createObjectURL(blob);

      if (downloadAnchorRef.current) {
        downloadAnchorRef.current.href = url;
        downloadAnchorRef.current.download =
          file.FileName ?? `image.${ext || 'jpg'}`;
        downloadAnchorRef.current.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      // silent
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={cn('LeadImageAttachment', {
        'LeadImageAttachment--out': !isIncoming,
        'LeadImageAttachment--deleted': isDeleted,
      })}
    >
      {isForwarded && (
        <div className="LeadImageAttachment__forwarded">
          <ShareAltOutlined className="LeadImageAttachment__forwarded-icon" />
          Пересланное сообщение
        </div>
      )}
      {sanitizedReplyHtml && (
        <div
          className="LeadImageAttachment__reply"
          data-reply-target-id={replyMessageId ?? undefined}
        >
          <div className="LeadImageAttachment__reply-indicator" />
          <div
            className="LeadImageAttachment__reply-text"
            dangerouslySetInnerHTML={{ __html: sanitizedReplyHtml }}
          />
        </div>
      )}
      <div className="LeadImageAttachment__preview">
        <a
          href=""
          className="LeadImageAttachment__thumbnail-link"
          onClick={handleOpen}
        >
          {file.Thumbnail ? (
            <img
              src={`data:image/jpeg;base64,${file.Thumbnail}`}
              alt={file.Description ?? file.FileName ?? ''}
              className="LeadImageAttachment__thumbnail"
            />
          ) : (
            <div className="LeadImageAttachment__thumbnail-placeholder">
              <FileImageOutlined className="LeadImageAttachment__thumbnail-placeholder-icon" />
            </div>
          )}
          {isLightboxLoading && (
            <div className="LeadImageAttachment__loading-overlay">
              <Spin size="small" />
            </div>
          )}
        </a>
        <Button
          className="LeadImageAttachment__download-btn"
          icon={<DownloadOutlined />}
          size="small"
          loading={isDownloading}
          onClick={handleDownload}
        />
      </div>
      <a
        ref={downloadAnchorRef}
        className="LeadImageAttachment__download-anchor"
      />
      <Modal
        centered
        className="LeadImageAttachment__modal"
        destroyOnHidden
        footer={null}
        open={isPreviewOpen}
        title={file.FileName ?? 'Изображение'}
        width="min(920px, 92vw)"
        onCancel={handlePreviewClose}
      >
        {previewSrc && (
          <img
            src={previewSrc}
            alt={file.Description ?? file.FileName ?? ''}
            className="LeadImageAttachment__modal-image"
          />
        )}
      </Modal>
      {sanitizedMessageHtml && (
        <div
          className="LeadImageAttachment__text"
          dangerouslySetInnerHTML={{ __html: sanitizedMessageHtml }}
        />
      )}
      {showStatus && (
        <div className="LeadImageAttachment__status">
          <span className="LeadImageAttachment__timestamp">
            {wasEdited && 'изменено '}
            {formattedTimestamp}
          </span>
          {statusIcon && (
            <span
              className="LeadImageAttachment__status-icon"
              style={{ color: statusColor }}
            >
              {statusIcon}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
